import { useState, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import PropTypes from 'prop-types';
import { Tooltip } from '../ui_elements/tooltip';
import { colors, zindex } from '../styles';
import useWindowSize from '../hooks/useWindowSize';
import useQueryParamState from '../hooks/useQueryParamState';
import { useEditorContext } from 'contexts/editor';
import {
  MultiPaneLayoutContainer,
  MultiPaneTabsList,
  MultiPaneTabContainer,
  StyledLink,
  MultiPanePanes,
  MainPane,
  MultiPaneLayoutSideBar,
  MultiPaneLayoutPanesContainer,
} from './baseComponents';

export const MultiPaneHeaderContainer = styled.div`
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.grey.secondaryDark};
  padding: ${(props) => props.compactHeader ? "0" : "22px 0"};
  position: ${(props) => props.compactHeader ? "sticky" : "relative"};
  top: 0;
  transition: padding 200ms linear;
  transition: ${(props) => props.isScrolled ? 'transform 0.3s ease-in-out' : 'none'};
  transform: ${(props) => props.isScrolled ? 'translateY(-100%)' : 'none'};
  z-index: ${zindex.modal.below};
  @media screen and (min-width: 769px) {
    height: ${(props) => props.compactHeader && "50px"};
  }
  @media screen and (max-width: 768px) {
    padding-bottom: 0;
  }
`;

export function MultiPaneTabsContainer({ tabs }) {
  const [singlePaneMode, setSinglePaneMode] = useState(false);
  const { width } = useWindowSize();
  const [rawActiveTab, setActiveTab, mergeSearch] = useQueryParamState('activeTab', "Requirements");
  const activeTab = rawActiveTab || "Requirements";
  const { editorPageState } = useEditorContext();
  const zenMode = editorPageState?.zenMode;

  const mainContent = tabs[0];

  useEffect(() => {
    const single = width < 1100;
    setSinglePaneMode(single);
    if (single) {
      setActiveTab(mainContent.name);
    } else if (!single && activeTab === mainContent.name) {
      setActiveTab("Requirements");
    }

  }, [width]);
  const sidebarTabs = singlePaneMode ? tabs : tabs.slice(1, tabs.length);
  const showSidebar = sidebarTabs.some(tab => tab.name === activeTab);
  const tooltipRight = singlePaneMode ? '0px' : '124px';
  const tooltipBottom = singlePaneMode ? '0px' : '1px';

  return (
    <>
      <MultiPaneLayoutPanesContainer singlePaneMode={singlePaneMode}>
        {!zenMode && (
          <MultiPaneTabsList singlePaneMode={singlePaneMode}>
            {sidebarTabs.map((tab) => {
              return (
                <StyledLink
                  to={{
                    search: mergeSearch({ activeTab: tab.name }),
                  }}
                  key={tab.name}
                >
                  <Tooltip
                    position="end"
                    content={tab.name}
                    right={tooltipRight}
                    bottom={tooltipBottom}
                    hide={singlePaneMode}
                  >
                    <MultiPaneTabContainer
                      active={tab.name === activeTab}
                      singlePaneMode={singlePaneMode}
                      className={tab.name === activeTab ? 'active-tab' : null}
                    >
                      {tab.label}
                    </MultiPaneTabContainer>
                  </Tooltip>
                </StyledLink>
              );
            })}
          </MultiPaneTabsList>
        )}
        <MultiPanePanes id='main-editor-pane'>
          <MainPane expand={mainContent.expand || singlePaneMode || !showSidebar}>
            {singlePaneMode
              ? (sidebarTabs.find(tab => tab.name === activeTab)?.content ?? mainContent.content)
              : mainContent.content}
          </MainPane>
        </MultiPanePanes>
        {!singlePaneMode && !zenMode && (
          <MultiPaneLayoutSideBar maxWidth="720px" show={showSidebar}>
            {showSidebar && sidebarTabs.find(tab => tab.name === activeTab)?.content}
          </MultiPaneLayoutSideBar>
        )}
      </MultiPaneLayoutPanesContainer>
    </>
  );
}

MultiPaneTabsContainer.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.element.isRequired,
      content: PropTypes.element.isRequired,
    })
  ).isRequired,
};

export function MultiPaneLayout({ children }) {
  return <MultiPaneLayoutContainer>{children}</MultiPaneLayoutContainer>;
}

MultiPaneLayout.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};
